<div class="container mx-auto p-6">
  <!-- Loading -->
  <div *ngIf="loading" class="flex justify-center items-center h-64">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>

  <!-- Contenido de la factura -->
  <div *ngIf="!loading && invoice" class="space-y-6 animate-fade-in">

    <!-- Header: Número de factura + botones -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div class="flex items-center gap-4">
          <div>
            <h2 class="text-2xl font-bold text-gray-800">{{ invoice.invoice_number }}</h2>
            <p class="text-sm text-gray-500 mt-0.5">Factura #{{ invoice.id }}</p>
          </div>
          <!-- Badges: Tipo + Estado + Rectificativa -->
          <div class="flex flex-wrap gap-2">
            <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold"
              [ngClass]="getTypeColor(invoice.invoice_type)">
              {{ getTypeLabel(invoice.invoice_type) }}
            </span>
            <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold"
              [ngClass]="getStatusColor(invoice.status)">
              {{ getStatusLabel(invoice.status) }}
            </span>
            <span *ngIf="isCorrectiveInvoice" class="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-orange-100 text-orange-800">
               RECTIFICATIVA
            </span>
          </div>
        </div>
        <div class="flex gap-2 self-start sm:self-center flex-wrap">
          <!-- Botón Marcar como Enviada (solo para borradores) -->
          <button *ngIf="!editMode && !showCorrectiveModal && invoice.status === 'draft'" (click)="markAsSent()"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-1.5">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Marcar como Enviada
          </button>

          <!-- Botón Marcar como Pagada (solo para facturas enviadas y no pagadas) -->
          <button *ngIf="!editMode && !showCorrectiveModal && invoice.status === 'sent' && !invoice.paid" (click)="markAsPaid()"
            class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center gap-1.5">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Marcar como Pagada
          </button>

          <!-- Botón Generar/Cancelar Rectificativa (solo para emitidas no rectificativas) -->
          <button *ngIf="!editMode && canCreateCorrective && !showCorrectiveModal" (click)="openCorrectiveModal()"
            class="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors duration-200 flex items-center gap-1.5">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Generar Rectificativa
          </button>

          <!-- Botón Descargar PDF -->
          <button *ngIf="!editMode && !showCorrectiveModal" (click)="downloadPdf()"
            [disabled]="downloadingPdf"
            class="px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors duration-200 flex items-center gap-1.5">
            <svg *ngIf="!downloadingPdf" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <svg *ngIf="downloadingPdf" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            {{ downloadingPdf ? 'Generando...' : 'Descargar PDF' }}
          </button>

          <button *ngIf="showCorrectiveModal" (click)="closeCorrectiveModal()"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center gap-1.5">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            Cancelar
          </button>

          <!-- Botón Volver -->
          <button
            [routerLink]="['/dashboard/administracion']"
            [queryParams]="{entity: 'invoices'}"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200">
            Volver a Facturas
          </button>
        </div>
      </div>
    </div>

    <!-- Modo Edición: formulario inline -->
    <div *ngIf="editMode" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <svg class="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        Editar Factura
      </h3>
      <app-generic-form
        [genericForm]="editForm"
        [selectedItem]="invoice"
        [showCancel]="true"
        (onCancel)="toggleEditMode()"
        (onFinished)="onEditFinished($event)">
      </app-generic-form>
    </div>

    <!-- Modo Crear Rectificativa: formulario inline -->
    <div *ngIf="showCorrectiveModal" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <svg class="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Generar Factura Rectificativa
      </h3>

      <!-- Información de la factura original y desglose de anulación -->
      <div class="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-6">
        <p class="text-sm text-blue-800 mb-2">
          <strong>Factura Original:</strong> {{ invoice?.invoice_number }}
        </p>
        <p class="text-sm text-blue-800 mb-3">
          Se creará una factura rectificativa por <strong>anulación completa</strong> con los siguientes importes:
        </p>
        <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-blue-900 bg-blue-100/50 rounded-lg p-3">
          <span class="font-medium">Base Imponible:</span>
          <span class="text-right font-semibold">-{{ invoice?.subtotal | number:'1.2-2' }} €</span>
          <span class="font-medium">IVA:</span>
          <span class="text-right font-semibold">-{{ invoice?.tax_amount | number:'1.2-2' }} €</span>
          <span *ngIf="invoice?.retention_amount" class="font-medium">Retención:</span>
          <span *ngIf="invoice?.retention_amount" class="text-right font-semibold">+{{ invoice?.retention_amount | number:'1.2-2' }} €</span>
          <span class="font-bold border-t border-blue-300 pt-1 mt-1">Total:</span>
          <span class="text-right font-bold border-t border-blue-300 pt-1 mt-1">-{{ invoice?.total | number:'1.2-2' }} €</span>
        </div>
      </div>

      <!-- Formulario -->
      <form [formGroup]="correctiveFormGroup" (ngSubmit)="onSubmitCorrectiveForm()" class="space-y-5">
        <div class="grid grid-cols-2 gap-4">
          <!-- Tipo de Rectificación -->
          <div class="col-span-2">
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              Tipo de Rectificación <span class="text-red-500">*</span>
            </label>
            <app-custom-dropdown
              formControlName="rectification_type"
              [options]="rectificationTypes"
              placeholder="Selecciona un tipo"
              [filter]="false"
              [showClear]="false">
            </app-custom-dropdown>
          </div>

          <!-- Motivo de la Rectificación -->
          <div class="col-span-2">
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              Motivo de la Rectificación <span class="text-red-500">*</span>
            </label>
            <textarea formControlName="rectification_reason" rows="3"
              class="w-full px-4 py-2.5 border-0 outline outline-1 outline-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:outline-primary transition-all duration-200"
              placeholder="Explica el motivo de la rectificación (mínimo 10 caracteres)"></textarea>
          </div>
        </div>

        <!-- Botones -->
        <div class="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button type="button" (click)="closeCorrectiveModal()"
            class="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200">
            Cancelar
          </button>
          <button type="submit" [disabled]="correctiveFormGroup.invalid || submittingCorrective"
            class="px-6 py-2.5 text-sm font-medium text-white bg-orange-600 rounded-xl hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200">
            <span *ngIf="!submittingCorrective">Crear Factura Rectificativa</span>
            <span *ngIf="submittingCorrective" class="flex items-center gap-2">
              <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creando...
            </span>
          </button>
        </div>
      </form>
    </div>

    <!-- Alerta: Información de factura rectificativa -->
    <div *ngIf="!editMode && isCorrectiveInvoice && invoice.original_invoice" class="bg-orange-50 border border-orange-200 rounded-xl p-6">
      <div class="flex items-start gap-3">
        <svg class="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div class="flex-1">
          <h4 class="text-sm font-semibold text-orange-900 mb-2">Esta es una Factura Rectificativa</h4>
          <div class="space-y-1.5">
            <p class="text-sm text-orange-800">
              <strong>Factura Original:</strong>
              <a [routerLink]="['/dashboard/administracion/invoices', invoice.original_invoice.id]"
                class="text-orange-600 hover:text-orange-700 underline ml-1">
                {{ invoice.original_invoice.invoice_number }}
              </a>
            </p>
            <p class="text-sm text-orange-800">
              <strong>Tipo de Rectificación:</strong>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ml-1"
                [ngClass]="getRectificationTypeColor(invoice.rectification_type)">
                {{ getRectificationTypeLabel(invoice.rectification_type) }}
              </span>
            </p>
            <p class="text-sm text-orange-800">
              <strong>Motivo:</strong> <span class="ml-1">{{ invoice.rectification_reason }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla: Facturas Rectificativas (si las hay) -->
    <div *ngIf="!editMode && correctiveInvoices.length > 0" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <svg class="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Factura Rectificativa
      </h3>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Número</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Motivo</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr *ngFor="let corrective of correctiveInvoices" class="hover:bg-gray-50 transition-colors">
              <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ corrective.invoice_number }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                {{ corrective.invoice_date }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                  [ngClass]="getRectificationTypeColor(corrective.rectification_type)">
                  {{ getRectificationTypeLabel(corrective.rectification_type) }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                {{ corrective.rectification_reason }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                {{ corrective.total | currency:'EUR':'symbol':'1.2-2' }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-right text-sm">
                <a [routerLink]="['/dashboard/administracion/invoices', corrective.id]"
                  class="text-primary hover:text-primary-600 font-medium">
                  Ver
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modo Visualización -->
    <div *ngIf="!editMode && !showCorrectiveModal">
      <!-- Grid principal: Info factura + Datos económicos -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

        <!-- Columna izquierda: Datos de la factura (2/3) -->
        <div class="lg:col-span-2 space-y-6">

          <!-- Datos generales -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <svg class="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Datos Generales
              </h3>
              <button *ngIf="!editMode && !showCorrectiveModal && canEdit" (click)="toggleEditMode()"
                class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors duration-200">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Editar
              </button>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Alumno -->
              <div>
                <span class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Alumno</span>
                <p class="text-sm font-medium text-gray-900">{{ invoice.user?.name || '—' }}</p>
                <p *ngIf="invoice.user?.email" class="text-xs text-gray-500 mt-0.5">{{ invoice.user.email }}</p>
              </div>
              <!-- Sociedad -->
              <div *ngIf="invoice.company">
                <span class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Sociedad</span>
                <p class="text-sm font-medium text-gray-900">{{ invoice.company?.name || '—' }}</p>
                <p *ngIf="invoice.company?.cif" class="text-xs text-gray-500 mt-0.5">CIF: {{ invoice.company.cif }}</p>
              </div>
              <!-- Fecha emisión -->
              <div>
                <span class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Fecha de Emisión</span>
                <p class="text-sm text-gray-900">{{ invoice.invoice_date | date:'dd/MM/yyyy' }}</p>
              </div>
              <!-- Fecha vencimiento -->
              <div>
                <span class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Fecha de Vencimiento</span>
                <p class="text-sm text-gray-900">{{ invoice.due_date ? (invoice.due_date | date:'dd/MM/yyyy') : '—' }}</p>
              </div>
              <!-- Método de pago -->
              <div>
                <span class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Método de Pago</span>
                <p class="text-sm text-gray-900">{{ invoice.payment_method ? getPaymentMethodLabel(invoice.payment_method) : '—' }}</p>
              </div>
              <!-- TPV -->
              <div *ngIf="invoice.pos_terminal">
                <span class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">TPV</span>
                <p class="text-sm text-gray-900">{{ invoice.pos_terminal.name }}</p>
              </div>
              <!-- Desglose fraccionado -->
              <div *ngIf="invoice.payment_method === 'split'" class="md:col-span-2">
                <span class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Desglose Fraccionado</span>
                <div class="flex gap-4 text-sm text-gray-900">
                  <span>Efectivo: <strong>{{ invoice.cash_amount | number:'1.2-2' }} €</strong></span>
                  <span>Tarjeta: <strong>{{ invoice.card_amount | number:'1.2-2' }} €</strong></span>
                </div>
              </div>
              <!-- Fecha de pago -->
              <div *ngIf="invoice.paid_date">
                <span class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Fecha de Pago</span>
                <p class="text-sm text-gray-900">{{ invoice.paid_date | date:'dd/MM/yyyy' }}</p>
              </div>
            </div>
          </div>

          <!-- Producto y Contrato asociados (si existen) -->
          <div *ngIf="invoice.product || invoice.contract"
            class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <svg class="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Datos Asociados
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Producto -->
              <div *ngIf="invoice.product">
                <span class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Producto</span>
                <p class="text-sm font-medium text-gray-900">{{ invoice.product.name }}</p>
                <p *ngIf="invoice.product.description" class="text-xs text-gray-500 mt-0.5">{{ invoice.product.description }}</p>
              </div>
              <!-- Contrato -->
              <div *ngIf="invoice.contract">
                <span class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Contrato</span>
                <p class="text-sm font-medium text-gray-900">#{{ invoice.contract.id }}</p>
                <p *ngIf="invoice.contract.registration_date" class="text-xs text-gray-500 mt-0.5">
                  Registrado: {{ invoice.contract.registration_date | date:'dd/MM/yyyy' }}
                </p>
              </div>
            </div>
          </div>

          <!-- Notas -->
          <div *ngIf="invoice.notes"
            class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <svg class="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Notas
            </h3>
            <p class="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-4 border border-gray-200">{{ invoice.notes }}</p>
          </div>
        </div>

        <!-- Columna derecha: Resumen económico (1/3) -->
        <div class="space-y-6">
          <!-- Desglose de precios -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-24">
            <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <svg class="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Resumen Económico
            </h3>

            <div class="space-y-3">
              <!-- Base imponible -->
              <div class="flex items-center justify-between py-2 border-b border-gray-100">
                <span class="text-sm text-gray-600">Base Imponible</span>
                <span class="text-sm font-semibold text-gray-900">{{ invoice.subtotal | currency:'EUR':'symbol':'1.2-2' }}</span>
              </div>
              <!-- IVA -->
              <div class="flex items-center justify-between py-2 border-b border-gray-100">
                <span class="text-sm text-gray-600">{{ invoice.tax_rate?.name || 'IVA' }} ({{ invoice.tax_rate?.rate || 0 }}%)</span>
                <span class="text-sm font-semibold text-gray-900">{{ invoice.tax_amount | currency:'EUR':'symbol':'1.2-2' }}</span>
              </div>
              <!-- Retención (si existe) -->
              <div *ngIf="invoice.retention_rate" class="flex items-center justify-between py-2 border-b border-gray-100">
                <span class="text-sm text-gray-600">{{ invoice.retention_rate?.name || 'Retención' }} ({{ invoice.retention_rate?.rate || 0 }}%)</span>
                <span class="text-sm font-semibold text-red-600">-{{ invoice.retention_amount | currency:'EUR':'symbol':'1.2-2' }}</span>
              </div>
              <!-- Total -->
              <div class="flex items-center justify-between py-3 mt-2 bg-gray-50 rounded-lg px-3">
                <span class="text-base font-bold text-gray-800">Total</span>
                <span class="text-xl font-bold text-gray-900">{{ invoice.total | currency:'EUR':'symbol':'1.2-2' }}</span>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Historial de Auditoría (solo en borradores y si hay cambios) -->
      <div *ngIf="!loadingAuditHistory && auditHistory.length > 0 && invoice.status === 'draft'" class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
        <!-- Header colapsable -->
        <button (click)="toggleAuditHistory()"
          class="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200">
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 class="text-lg font-semibold text-gray-800">Historial de Cambios</h3>
            <span class="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-semibold">
              {{ auditHistory.length }}
            </span>
          </div>
          <svg class="w-5 h-5 text-gray-400 transition-transform duration-200"
            [class.rotate-180]="showAuditHistory"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <!-- Contenido del historial -->
        <div *ngIf="showAuditHistory" class="border-t border-gray-200">
          <!-- Loading -->
          <div *ngIf="loadingAuditHistory" class="flex justify-center items-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>

          <!-- Lista de auditoría -->
          <div *ngIf="!loadingAuditHistory && auditHistory.length > 0" class="divide-y divide-gray-100">
            <div *ngFor="let log of auditHistory"
              class="p-6 hover:bg-gray-50 transition-colors duration-150">

              <div class="flex gap-4">
                <!-- Icono de acción -->
                <div class="flex-shrink-0 w-10 h-10 rounded-full bg-white border-2 flex items-center justify-center text-gray-700">
                  <span [innerHTML]="getSafeActionIcon(log.action)"></span>
                </div>

                <!-- Contenido -->
                <div class="flex-1 min-w-0">
                  <!-- Header: Acción y usuario -->
                  <div class="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <p class="text-sm font-semibold text-gray-900">{{ log.action_description }}</p>
                      <p class="text-xs text-gray-600 mt-0.5">
                        <span *ngIf="log.user">por <span class="font-medium">{{ log.user.name }}</span></span>
                        <span *ngIf="!log.user" class="italic">Usuario eliminado</span>
                      </p>
                    </div>
                    <div class="text-right flex-shrink-0">
                      <p class="text-xs text-gray-500">{{ log.created_at }}</p>
                      <p class="text-xs text-gray-400 mt-0.5">{{ log.created_at_human }}</p>
                    </div>
                  </div>

                  <!-- Campos cambiados -->
                  <div *ngIf="log.changes && log.changes.length > 0" class="mt-3 space-y-2">
                    <div *ngFor="let change of log.changes"
                      class="bg-white rounded-lg border border-gray-200 p-3 text-xs">
                      <p class="font-semibold text-gray-700 mb-2">{{ change.field_name }}</p>
                      <div class="grid grid-cols-2 gap-3">
                        <div>
                          <p class="text-gray-500 mb-1">Anterior:</p>
                          <p class="text-gray-900 font-medium break-words">{{ change.old_value }}</p>
                        </div>
                        <div>
                          <p class="text-gray-500 mb-1">Nuevo:</p>
                          <p class="text-gray-900 font-medium break-words">{{ change.new_value }}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- IP (solo visible para admins) -->
                  <p *ngIf="log.ip_address" class="text-xs text-gray-400 mt-2">
                    IP: {{ log.ip_address }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Sin historial -->
          <div *ngIf="!loadingAuditHistory && auditHistory.length === 0" class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="text-gray-500 text-sm">No hay historial de cambios disponible</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Estado vacío -->
  <div *ngIf="!loading && !invoice" class="text-center py-16">
    <svg class="mx-auto h-16 w-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
    <p class="text-gray-500 text-lg font-medium">Factura no encontrada</p>
    <p class="text-gray-400 text-sm mt-1">La factura solicitada no existe o no tienes acceso</p>
    <button
      [routerLink]="['/dashboard/administracion']"
      [queryParams]="{entity: 'invoices'}"
      class="mt-4 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors duration-200">
      Volver a Facturas
    </button>
  </div>

</div>

<!-- Modal de confirmación -->
<app-confirmation
  *ngIf="showConfirmation"
  [visible]="showConfirmation"
  [question]="confirmationQuestion"
  confirmText="Sí, continuar"
  cancelText="Cancelar"
  (confirm)="onConfirm()"
  (cancel)="onCancelConfirmation()">
</app-confirmation>

<!-- Diálogo: Crear nueva factura tras rectificativa -->
<app-confirmation
  *ngIf="showNewInvoiceDialog"
  [visible]="showNewInvoiceDialog"
  question="La factura rectificativa se ha creado correctamente. ¿Deseas crear una nueva factura para este alumno?"
  confirmText="Sí, crear nueva factura"
  cancelText="No, volver al detalle"
  (confirm)="onConfirmCreateNewInvoice()"
  (cancel)="onCancelCreateNewInvoice()">
</app-confirmation>
